import {Component, ContentChildren, EventEmitter, Input, OnInit, Output, QueryList} from '@angular/core';
import {RcTableHeaderComponent} from './rc-table-header.component';
import {RcTableRowComponent} from './rc-table-row.component';
import {RcTableDrawerComponent} from './rc-table-drawer.component';
import {RcTableFooterRowComponent} from './rc-table-footer-row.component';
import {RcTableRowExtensionComponent} from './rc-table-row-extension.component';

@Component({
    selector: 'lib-rc-table',
    template: `<div class="rc-table-wrapper" role="table">
    <div class="rc-table-headers-wrapper" role="rowgroup" lib-rc-inview (rcInviewCallback)="headerInView($event)">
        <ng-container *ngFor="let header of headers.toArray(); let i = index">
            <div class="rc-table-header-wrapper {{getSortClasses(header)}}" role="columnheader"
                 [ngStyle]="{width: header.data.width}"
                 (click)="headerClick(header)"
                 >
                <div class="rc-table-header-cell-content-wrapper">
                    <ng-container [ngTemplateOutlet]="header.tableHeader" [ngTemplateOutletContext]="{data: header.data, headerClickCallback: header.headerClickCallback}"></ng-container>
                </div>
                <span *ngIf="isSorted(header)" class="sort-indicator "></span>
            </div>
        </ng-container>
    </div>
    <div class="rc-table-body-wrapper" role="rowgroup" lib-rc-inview (rcInviewCallback)="bodyInView($event)">
        <ng-container *ngFor="let row of rows.toArray(); let i = index">
            <ng-container *ngIf="showRowByPage(i)">
                <div class="rc-table-row-wrapper {{gridRows[i].rowCustomClass}}" [attr.tabindex]="gridConfig.haveDrawer ? 0 : null"
                     (keydown)="onKeyDown($event, row, i)" (click)="rowClick(row, i, $event)" role="row"
                     [ngClass]="{'open': gridRows[i].drawerOpened, 'disabled': row.rowDisabled, 'with-drawer': gridConfig.haveDrawer && !row.drawerDisabled}"
                     [attr.aria-expanded]="gridRows[i].drawerOpened"
                >
                    <ng-container [ngTemplateOutlet]="row.tableRow" [ngTemplateOutletContext]="{data: gridConfig, rowData: row.rowData}"></ng-container>
                </div>
                <div class="rc-table-row-extension-wrapper" *ngIf="gridConfig.hasRowExtension && gridRows[i].showRowExtension">
                  <ng-container [ngTemplateOutlet]="extensions.toArray()[i].rowExtension" [ngTemplateOutletContext]="{rowData: row.rowData}"></ng-container>
                </div>
                <div class="rc-table-drawer-wrapper" *ngIf="gridConfig.haveDrawer && gridRows[i].drawerOpened">
                <ng-container [ngTemplateOutlet]="drawers.toArray()[i].tableDrawer" [ngTemplateOutletContext]="{rowData: row.rowData}"></ng-container>
            </div>
            </ng-container>
        </ng-container>
        <ng-container *ngFor="let row of CCFooterRows.toArray(); let i = index">
          <div class="rc-table-footer-row-wrapper" role="row">
            <ng-container [ngTemplateOutlet]="row.tableFooterRow" [ngTemplateOutletContext]="{data: gridConfig, rowData: row.rowData}"></ng-container>
          </div>
        </ng-container>
    </div>
</div>
`,
    styles: [`.rc-table-wrapper .rc-table-headers-wrapper .rc-table-header-wrapper{display:inline-block;position:relative}.rc-table-wrapper .rc-table-headers-wrapper .rc-table-header-wrapper .rc-table-header-cell-content-wrapper{display:inline-block}.rc-table-wrapper .rc-table-headers-wrapper .rc-table-header-wrapper.sorted.asc .sort-indicator:before,.rc-table-wrapper .rc-table-headers-wrapper .rc-table-header-wrapper.sorted.desc .sort-indicator:before{content:''}.rc-table-wrapper .rc-table-row-wrapper.with-drawer{cursor:pointer}`]
})

export class RcTableComponent implements OnInit {

    @ContentChildren(RcTableHeaderComponent) headers: QueryList<RcTableHeaderComponent>;
    @ContentChildren(RcTableRowComponent) rows: QueryList<RcTableRowComponent>;
    @ContentChildren(RcTableFooterRowComponent) CCFooterRows: QueryList<RcTableFooterRowComponent>;
    @ContentChildren(RcTableDrawerComponent) drawers: QueryList<RcTableDrawerComponent>;
    @ContentChildren(RcTableRowExtensionComponent) extensions: QueryList<RcTableRowExtensionComponent>;
    @Input() gridConfig: any = {};
    @Input() gridRows: any[] = [];
    @Input() pageNumber: number = 0;
    @Input() footerRows: any[] = [];
    @Input() serverSideSorting = false;

    @Output() rowClickCallback: EventEmitter<any> = new EventEmitter();
    @Output() headerClickCallback: EventEmitter<any> = new EventEmitter();
    @Output() showStickyHeaderCallback: EventEmitter<any> = new EventEmitter();

    public asc = true;
    public sortedHeader: any = {};
    public headerIsInView: boolean;
    public bodyIsInView: boolean;


    ngOnInit() {

    }

    headerClick(header) {
        if (header.data.sortable) {
            if (!this.serverSideSorting) {
                this.gridRows = this.sortArrayByKey(this.gridRows, header.data.field, this.asc);
            }
            this.asc = !this.asc;
            this.sortedHeader = header.data;
        }
        if (this.headerClickCallback.observers.length) {
            const sortObj = {
                col: header.data,
                direction: this.asc ? 'asc' : 'desc'
            };
            this.headerClickCallback.emit(sortObj);
        }
    }
    rowClick(row, i, event) {
        if (this.rowClickCallback.observers.length) {
            this.rowClickCallback.emit(row.rowData);
        }
        if (event.rcTableCol && event.rcTableCol.preventDrawerToggle) {
            return;
        }
        if (this.gridConfig.haveDrawer && !row.drawerDisabled) {
            this.toggleDrawer(i);
        }
    }

    sortArrayByKey(arr: any[], key: string, asc = true) {
        if (asc) {
            arr.sort((a, b) => {
              const aKey =  typeof a[key] === 'string' ? a[key].toLowerCase() : a[key];
              const bKey =  typeof b[key] === 'string' ? b[key].toLowerCase() : b[key];
                return (aKey > bKey ? -1 : 1);
            });
        } else {
            arr.sort((a, b) => {
              const aKey =  typeof a[key] === 'string' ? a[key].toLowerCase() : a[key];
              const bKey =  typeof b[key] === 'string' ? b[key].toLowerCase() : b[key];
                return (aKey < bKey ? -1 : 1);
            });
        }
        return arr;
    }

    getSortClasses(header) {
        let classes = [];
        if (this.isSorted(header)) {
            classes.push('sorted');
            classes.push(this.asc ? 'asc' : 'desc');
        }
        return classes.join(' ');
    }
    isSorted(header) {
        return header.data.field === this.sortedHeader.field;
    }

    toggleDrawer(i) {
        this.gridRows[i].drawerOpened = !this.gridRows[i].drawerOpened;

    }

    setInviewOnRow(row, $event) {
        row.show = $event;
    }

    onKeyDown($event, row, i) {
        if ($event.which === 13) {
            if (this.gridConfig.haveDrawer) {
                this.rowClick(row, i, $event);
            }
        }
    }

    showRowByPage(i) {
        if (this.gridConfig.hasClientPaging) {
            return i >= this.pageNumber * this.gridConfig.pageSize && i < (this.pageNumber + 1) * this.gridConfig.pageSize;
        }
        return true;
    }

  headerInView($event) {
    this.headerIsInView = $event;
    this.showStickyHeader();

  }

  bodyInView($event) {
    this.bodyIsInView = $event;
    this.showStickyHeader();
  }

  showStickyHeader() {
    if (this.showStickyHeaderCallback.observers.length) {
      const stickyHeader = !this.headerIsInView && this.bodyIsInView;
      this.showStickyHeaderCallback.emit(stickyHeader);
    }
  }
}
